import React, {useState, useEffect, useRef, useCallback} from "react";
import axios from "axios";
import CartItem from "./CartItem";
import "./CentralArea.css";
import {motion, AnimatePresence} from "framer-motion";
import {ClipLoader} from "react-spinners";

const swipeVariants = {
    enter: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0 }),
    center: { x: 0, opacity: 1, transition: {duration: 0.3} },
    exit: (direction) => ({ x: direction > 0 ? -100 : 100, opacity: 0, transition: {duration: 0.3} }),
};

// --- VARIABILI GLOBALI (Tirate fuori dai componenti) ---
const isProd = process.env.REACT_APP_PROD === 'true';
const apiProdUrl = process.env.REACT_APP_API_PROD_URL;
const apiStageUrl = process.env.REACT_APP_API_STAGE_URL_V2;
const apiToken = process.env.REACT_APP_API_TOKEN;
const BASE_URL = isProd ? apiProdUrl : apiStageUrl;
const CDN = "https://cdn.psicopaticiservice.com/";

// --- COMPONENTI SPOSTATI ALL'ESTERNO ---

const SkeletonCard = () => (
    <div className="product-card skeleton">
        <div className="skeleton-image"></div>
        <div className="skeleton-details">
            <div className="skeleton-line"></div>
            <div className="skeleton-line short"></div>
        </div>
    </div>
);

const Checkout = ({ cartItems, setCartItems, totaleCarrello, step }) => {
    const [showNewsletter, setShownewsletter] = useState(true);

    const removeItemFromCart = (id) => {
        setTimeout(() => {
            setCartItems((items) => items.filter((prevItem) => prevItem.id !== id));
        }, 150);
    };
    
    const handleNewsletter = (result) => {
        setTimeout(() => {
            setShownewsletter(false);
            const newsletterEl = document.getElementById("newsletter");
            if (newsletterEl) newsletterEl.checked = result;
        }, 150);
    };
    
    return (
        <div className="checkout">
            <div className="d-flex justify-content-between align-items-center" style={{padding: "5px 10px 5px 10px", marginBottom: "10px"}}>
                <div className="d-flex align-items-center">
                    <i style={{fontSize: "25px", marginTop: "px"}} className="fa-solid fa-cart-shopping me-1"></i>
                    <h3 style={{margin: "0", marginTop: "5px", fontWeight: "700"}}>Carrello</h3>
                </div>
                <div>
                    <h3 style={{margin: "0", fontWeight: "700"}}>Totale: {totaleCarrello} €</h3>
                </div>
            </div>
            
            {cartItems.length === 0 ? (
                <div style={{textAlign: "center", marginTop: "20px"}}>
                    <i className="fas fa-shopping-cart fa-3x" style={{color: "#ccc"}}></i>
                    <p style={{fontSize: "18px", color: "#999", marginTop: "10px"}}>
                        Il carrello è vuoto. Aggiungi un prodotto prima di procedere al pagamento.
                    </p>
                </div>
            ) : (
                <div className={"cart-parent"}>
                    <div className="cart-container" style={{maxHeight: "45vh", paddingBottom: "25px"}}>
                        {cartItems.map((item) => (
                            <CartItem key={item.id} id={item.id} item={item} onRemove={removeItemFromCart}/>
                        ))}
                    </div>
                    
                    <motion.div
                        initial={{y: 100, opacity: 0}}
                        animate={{y: 0, opacity: 1}}
                        exit={{y: 100, opacity: 1}}
                        transition={{duration: 0.5, ease: "easeInOut"}}
                        className={`rounded-top-3 fixed-message ${step !== 2 || !showNewsletter ? 'hidden' : ''}`}
                    >
                        <p className="newsletter-advice">Se ti iscrivi alla newsletter riceverai un ulteriore 5% di sconto!</p>
                        <div className="d-flex align-items-center gap-1 ms-2">
                            <button style={{height: "40px"}} className={"btn btn-white"} onClick={() => handleNewsletter(true)}> Accetta </button>
                            <button style={{height: "40px"}} className={"btn btn-white"} onClick={() => handleNewsletter(false)}> Chiudi </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

const Catalog = ({ slug, navigateToView, totemId }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const catalogRef = useRef(null);

    const scrollCatalogToTop = useCallback(() => {
        const catalog = catalogRef.current;
        if (!catalog) return;
        catalog.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, []);

    useEffect(() => {
        scrollCatalogToTop();
    }, [slug, scrollCatalogToTop]);
    const loadedKeyRef = useRef(null);

    const preloadImage = (src) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = src;
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
        });
    };

    useEffect(() => {
        const key = `${totemId || ""}::${slug || ""}`;
        if (loadedKeyRef.current === key) {
            console.log(`[CATALOG] SKIP duplicate fetch key=${key}`);
            return;
        }
        loadedKeyRef.current = key;

        let isMounted = true;
        setLoading(true);
        const runId = Math.random().toString(36).slice(2, 8);
        console.log(`[CATALOG ${runId}] useEffect TRIGGER | slug=${JSON.stringify(slug)} totemId=${JSON.stringify(totemId)} key=${key}`);

        const fetchProducts = async () => {
            try {
                const isHome = !slug || slug === "null" || slug === "undefined" || slug === "home-totem";
                console.log(`[CATALOG ${runId}] isHome=${isHome} totemId=${totemId}`);

                if (isHome && totemId) {
                    // Homepage: fetch configured products from totem backend, then details from official API
                    const homepageRes = await fetch(`https://api.psicopatici.com/api/totems/${totemId}/homepage`);
                    if (!homepageRes.ok) throw new Error("Homepage fetch failed");
                    const homepageData = await homepageRes.json();
                    console.log(`[CATALOG ${runId}] homepage raw response:`, JSON.parse(JSON.stringify(homepageData)));
                    const homepageProducts = (homepageData.products || []).sort((a, b) => a.order - b.order);
                    console.log(`[CATALOG ${runId}] homepage products array:`, JSON.parse(JSON.stringify(homepageProducts)));
                    console.log(`[CATALOG ${runId}] slug_keys:`, homepageProducts.map(hp => hp.slug_key));

                    const productPromises = homepageProducts
                        .filter(hp => {
                            const ok = !!hp.slug_key;
                            if (!ok) console.warn(`[CATALOG ${runId}] FILTERED OUT (no slug_key):`, hp);
                            return ok;
                        })
                        .map(hp => {
                            const url = `${BASE_URL}/products/slug/${hp.slug_key}`;
                            console.log(`[CATALOG ${runId}] FETCH URL:`, url, "| slug_key:", JSON.stringify(hp.slug_key));
                            return axios.get(url, {
                                headers: { 'X-Site-Token': apiToken },
                            })
                                .then(res => {
                                    console.log(`[CATALOG ${runId}] OK ${hp.slug_key} → data.slug=${JSON.stringify(res.data?.data?.slug)}`);
                                    return ({ data: res.data?.data, slug_key: hp.slug_key });
                                })
                                .catch(err => {
                                    console.error(`[CATALOG ${runId}] FAIL ${hp.slug_key}:`, err?.message);
                                    return null;
                                });
                        });

                    const settled = await Promise.all(productPromises);
                    console.log(`[CATALOG ${runId}] settled count=${settled.length}`, settled.map(s => s ? {slug_key: s.slug_key, hasData: !!s.data, dataKeys: s.data ? Object.keys(s.data) : null} : null));

                    const fetched = settled
                        .filter(item => item?.data)
                        .map(({ data: p, slug_key }) => {
                            console.log(`[CATALOG ${runId}] MAPPING p.id=${p.id} p.slug=${JSON.stringify(p.slug)} slug_key=${JSON.stringify(slug_key)}`);
                            const finalSlug = p.slug || slug_key;
                            if (!finalSlug) console.warn(`[CATALOG ${runId}] PRODUCT ${p.id} HAS NO SLUG! p.slug=${JSON.stringify(p.slug)} slug_key=${JSON.stringify(slug_key)}`);
                            const obj = {
                                id: p.id,
                                image: CDN + p.featured_image,
                                name: p.name,
                                price: p.price,
                                sale_price: p.sale_price,
                                slug: finalSlug,
                            };
                            console.log(`[CATALOG ${runId}] MAPPED obj:`, obj);
                            return obj;
                        });

                    console.log(`[CATALOG ${runId}] FINAL mapped products:`, fetched.map(p => ({id: p.id, slug: p.slug, name: p.name})));

                    await Promise.all(fetched.map(p => preloadImage(p.image)));
                    if (!isMounted) {
                        console.log(`[CATALOG ${runId}] UNMOUNTED before setProducts`);
                        return;
                    }
                    console.log(`[CATALOG ${runId}] setProducts with ${fetched.length} items`);
                    setProducts(fetched);
                } else {
                    // Category page: fetch by slug
                    const currentSlug = (slug && slug !== "null" && slug !== "undefined") ? slug : "home-totem";
                    const url = `${BASE_URL}/product-category-priority/slug/${currentSlug}`;

                    const response = await axios.get(url, {
                        params: { per_page: 100, customization: "2d_customizable" },
                        headers: { 'X-Site-Token': apiToken },
                    });

                    if (!isMounted) return;

                    const categoryProducts = Array.isArray(response.data?.data)
                        ? response.data.data
                        : response.data?.data?.products || [];

                    if (categoryProducts.length > 0) {
                        const fetchedProducts = categoryProducts.map((product) => ({
                            id: product.id,
                            image: CDN + product.image,
                            name: product.name,
                            price: product.price,
                            sale_price: product.sale_price,
                            slug: product.slug,
                        }));

                        const imagePromises = fetchedProducts.map(product => preloadImage(product.image));
                        await Promise.all(imagePromises);

                        if (!isMounted) return;
                        setProducts(fetchedProducts);
                        requestAnimationFrame(scrollCatalogToTop);
                    } else {
                        if (!isMounted) return;
                        setProducts([]);
                        requestAnimationFrame(scrollCatalogToTop);
                    }
                }
            } catch (err) {
                if (!isMounted) return;
                setError("Errore caricamento");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchProducts();

        return () => {
            isMounted = false;
        };
    }, [slug, totemId]);

    return (
        <div className="catalog" ref={catalogRef}>
            <div className="product-grid">
                {loading ? (
                    Array.from({ length: 8 }).map((_, index) => (
                        <SkeletonCard key={`sk-${index}`} />
                    ))
                ) : error ? (
                    <p>{error}</p>
                ) : (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} style={{ display: 'contents' }}>
                        {products.map((product) => {
                            const price = parseFloat(product.price);
                            const salePrice = product.sale_price ? parseFloat(product.sale_price) : null;
                            const isOnSale = salePrice && salePrice < price;
                            const discountPercent = isOnSale ? Math.round(((price - salePrice) / price) * 100) : 0;

                            return (
                                <div key={product.id} onClick={() => {
                                    console.log(`[CATALOG CLICK] product.id=${product.id} product.slug=${JSON.stringify(product.slug)} product.name=${JSON.stringify(product.name)}`);
                                    if (!product.slug) console.error(`[CATALOG CLICK] BUG! click su prodotto SENZA slug:`, product);
                                    navigateToView("product", product.slug);
                                }} className="product-card">
                                    <div className="product-image">
                                        <img src={product.image} alt={product.name} />
                                        {isOnSale && (
                                            <div style={{ position: 'absolute', top: '10px', right: '10px', backgroundColor: 'black', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                -{discountPercent}%
                                            </div>
                                        )}
                                    </div>
                                    <div className="product-details">
                                        <h4 className="product-name">{product.name}</h4>
                                        {isOnSale ? (
                                            <div className="d-flex align-items-center gap-2">
                                                <p className="product-price mb-0" style={{color: 'black'}}>
                                                    <span style={{fontWeight: "700"}}>{salePrice.toFixed(2)}€</span>
                                                </p>
                                                <p className="product-price mb-0" style={{textDecoration: 'line-through', color: '#999', fontSize: '0.9em', marginLeft: '5px'}}>
                                                    {price.toFixed(2)}€
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="product-price"><span style={{fontWeight: "700"}}>{price.toFixed(2)}€</span></p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </motion.div>
                )}
            </div>
        </div>
    );
};

// *** PRODUCT EDITOR ***
const ProductEditor = ({ product, slug, selectedOptions, currentIndex, setCurrentIndex, disabledParts = {} }) => {
    const [layers, setLayers] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    const fullImageOrder = ["image", "image_right", "image_back", "image_left"];
    const [validViews, setValidViews] = useState(fullImageOrder);

    const swipeConfidenceThreshold = 10000; 
    
    const swipePower = (offset, velocity) => {
        return Math.abs(offset) * velocity;
    };

    const preloadImage = (src) =>
        new Promise((resolve) => {
            const img = new Image();
            img.src = src;
            img.onload = () => resolve(src);
            img.onerror = () => resolve(null);
        });

    useEffect(() => {
        if (!product || !product.options) return;
        const computedViews = fullImageOrder.filter(viewKey => {
            return product.options.some(opt => {
                return opt.values.some(val => 
                    val[viewKey] && val[viewKey] !== "null" && val[viewKey] !== "undefined" && val[viewKey] !== ""
                );
            });
        });
        if (computedViews.length === 0) computedViews.push("image");
        setValidViews(computedViews);
    }, [product]);

    const buildLayers = async (idx) => {
        if (!product || !product.options || validViews.length === 0) {
            setLayers([]);
            return;
        }

        const safeIndex = idx % validViews.length;
        const fieldName = validViews[safeIndex]; 
        const tempLayers = [];

        for (const opt of product.options) {
            let selectedValueId = null;
            if (selectedOptions && selectedOptions[opt.id] !== undefined && selectedOptions[opt.id] !== null) {
                selectedValueId = String(selectedOptions[opt.id]);
            }
            if (selectedValueId === "none") continue; 

            if (disabledParts[opt.name] && disabledParts[opt.name].length > 0) {
                continue;
            }

            let value = null;
            if (selectedValueId) {
                value = opt.values?.find((v) => String(v.id) === selectedValueId);
            } 
            if (!value && !selectedValueId) {
                value = opt.values?.find((v) => String(v.is_default) === "1" || v.is_default === true);
            }

            if (value) {
                let imgPath = value[fieldName];
                if (imgPath && imgPath !== "null" && imgPath !== "" && imgPath !== "undefined") {
                    const fullUrl = imgPath.startsWith("http") ? imgPath : CDN + imgPath;
                    tempLayers.push(fullUrl);
                }
            }
        }

        await Promise.all(tempLayers.map(src => preloadImage(src)));
        setLayers(tempLayers);
    };

    useEffect(() => {
        if (!product || product.slug !== slug) return;
        if (validViews.length === 0) return;
        buildLayers(currentIndex).finally(() => {
            setIsInitialLoad(false);
        });
    }, [product, slug, selectedOptions, currentIndex, disabledParts, validViews]);

    const handleSwipe = (direction) => {
        if (validViews.length === 0) return;
        let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
        if (newIndex >= validViews.length) newIndex = 0;
        if (newIndex < 0) newIndex = validViews.length - 1;
        setCurrentIndex(newIndex);
    };

    if (isInitialLoad && layers.length === 0) {
        return (
            <div className="product-editor d-flex justify-content-center align-items-center" style={{height: '100%'}}>
                <ClipLoader color={"#000000"} loading={true} size={50} cssOverride={{ borderWidth: "5px" }} />
            </div>
        );
    }

    return (
        <div className="product-editor" style={{ touchAction: "none", cursor: "grab" }}>
            <motion.div 
                className="image-gallery"
                onPanEnd={(e, info) => {
                    const swipe = swipePower(info.offset.x, info.velocity.x);
                    if (swipe < -swipeConfidenceThreshold) {
                        handleSwipe('prev'); 
                    } else if (swipe > swipeConfidenceThreshold) {
                        handleSwipe('next'); 
                    }
                }}
            >
                <div className="layers-container" style={{ pointerEvents: "none" }}> 
                    {layers.length > 0 ? (
                        layers.map((src, index) => (
                            <motion.img
                                key={`layer-${index}`} 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                                src={src}
                                className="layer-image"
                                style={{ zIndex: index + 10 }} 
                                alt={`layer-${index}`}
                            />
                        ))
                    ) : (
                        <img 
                            src={product.image ? (product.image.startsWith('http') ? product.image : CDN + product.image) : ""} 
                            className="layer-image" 
                            style={{zIndex: 1}}
                            alt="base-product"
                        />
                    )}
                </div>
            </motion.div>
        </div>
    );
};

// --- COMPONENTE PRINCIPALE CENTRALAREA ---
const CentralArea = ({
    product,
    cartItems,
    setCartItems,
    view,
    activeImages,
    activeViewIndex,
    setActiveViewIndex,
    selectedOptions,
    slug,
    navigateToView,
    step,
    disabledParts,
    totemId,
}) => {
    const [iframeUrl] = useState("https://partner.psicopatici.com");
    const [totaleCarrello, setTotaleCarrello] = useState("0.00");
    
    useEffect(() => {
        const total = cartItems.reduce((acc, item) => {
            const originalPrice = parseFloat(item.price);
            const salePrice = item.sale_price ? parseFloat(item.sale_price) : null;
            const basePriceToUse = (salePrice !== null && salePrice < originalPrice) 
                ? salePrice 
                : originalPrice;

            const itemQty = parseInt(item.qty);
            let optionPriceAdjustment = 0;
            
            if (item.options && item.options.optionCartValue) {
                if (Array.isArray(item.options.optionCartValue)) {
                    item.options.optionCartValue.forEach(container => {
                        Object.values(container).forEach(optArr => {
                            optArr.forEach(opt => optionPriceAdjustment += parseFloat(opt.affect_price || 0));
                        });
                    });
                } else {
                    Object.values(item.options.optionCartValue).forEach(optionItems => {
                        optionItems.forEach(option => {
                            optionPriceAdjustment += parseFloat(option.affect_price || 0);
                        });
                    });
                }
            }
            return acc + ((basePriceToUse + optionPriceAdjustment) * itemQty);
        }, 0);
        
        setTotaleCarrello(total.toFixed(2));
    }, [cartItems]);
    
    return (
        <div className="central-area">
            {view === "categories" && (
                <Catalog slug={slug} navigateToView={navigateToView} totemId={totemId} />
            )}
            
            {view === "product" && (
                <ProductEditor 
                    product={product}
                    slug={slug}
                    selectedOptions={selectedOptions}
                    currentIndex={activeViewIndex} 
                    setCurrentIndex={setActiveViewIndex}
                    disabledParts={disabledParts} 
                />
            )}
            
            {view === "checkout" && (
                <Checkout 
                    cartItems={cartItems} 
                    setCartItems={setCartItems} 
                    totaleCarrello={totaleCarrello} 
                    step={step} 
                />
            )}
            
            {view === "iframe" && (
                <iframe
                    src={iframeUrl}
                    title="External Content"
                    style={{width: "100%", height: "99%", border: "none"}}
                ></iframe>
            )}
        </div>
    );
};

export default CentralArea;
